// frontend/src/components/auth/VerifyEmail.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authService } from "../../services/auth";
import { analyticsService } from "../../services/analytics";
import VerificationPending from "./VerificationPending";

function VerifyEmail({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const mode = searchParams.get("mode");
  const pendingEmail = searchParams.get("email") || "";

  const [status, setStatus] = useState(token ? "verifying" : mode === "pending" ? "pending" : "idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await authService.verifyEmailToken(token);
        if (cancelled) return;
        analyticsService.track("user_email_verified", { provider: "email" });
        setStatus("success");
        await onLogin(data.is_new_user || false);
        setTimeout(() => navigate("/"), 1500);
      } catch (err) {
        if (cancelled) return;
        analyticsService.track("user_email_verification_failed", {
          reason: err.message || "unknown",
        });
        setStatus("error");
        setError(err.message || "Verification interrupted. Try logging in to complete verification.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, navigate, onLogin]);

  const handleResend = async () => {
    if (!pendingEmail) return { success: false, error: "No email address provided." };
    try {
      await authService.resendVerificationEmail(pendingEmail);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (status === "pending" || (!token && mode !== "pending")) {
    return (
      <div className="flex-1 flex items-start justify-center p-6">
        <VerificationPending
          email={pendingEmail || "your email"}
          onResend={handleResend}
          onChangeEmail={handleBackToLogin}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-start justify-center p-6">
      <div
        className="max-w-md w-full p-6 text-center"
        style={{
          color: "var(--text-primary, #333333)",
          background: "var(--bg-secondary, #f5f5f5)",
          borderRadius: "16px",
          boxShadow: "0 2px 16px rgba(0, 0, 0, 0.08)",
        }}
      >
        {status === "verifying" && (
          <>
            <div
              className="mx-auto mb-4"
              style={{
                width: 48,
                height: 48,
                border: "4px solid rgba(46, 125, 50, 0.2)",
                borderTopColor: "var(--pista-green-primary, #2E7D32)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p>Verifying your email...</p>
          </>
        )}

        {status === "success" && (
          <>
            <div
              className="mx-auto mb-4 flex items-center justify-center"
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(46, 125, 50, 0.12)",
                fontSize: "32px",
              }}
            >
              ✅
            </div>
            <h2 style={{ margin: "0 0 8px" }}>Email verified!</h2>
            <p style={{ color: "var(--text-secondary, #666666)" }}>
              Redirecting you to the app...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div
              className="mx-auto mb-4 flex items-center justify-center"
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(211, 47, 47, 0.12)",
                fontSize: "32px",
              }}
            >
              ⚠️
            </div>
            <h2 style={{ margin: "0 0 8px" }}>Verification interrupted: try login again</h2>
            <p style={{ color: "var(--text-secondary, #666666)", marginBottom: 24 }}>
              {error}
            </p>
            <button
              type="button"
              onClick={handleBackToLogin}
              style={{
                padding: "12px 24px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
                background: "var(--pista-button-bg, #2E7D32)",
                color: "var(--pista-button-text, #FFFFFF)",
              }}
            >
              Go to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
